/**
 * Time zone helpers built on `Intl`, so the audit can cut days and print times in the fleet's
 * zone (the Geotab user's setting) regardless of where the auditor's computer is.
 */

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let cached = formatters.get(timeZone);
  if (!cached) {
    cached = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, cached);
  }
  return cached;
}

/** True when `Intl` knows the zone id. */
export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of formatter(timeZone).formatToParts(date)) parts[part.type] = part.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** `yyyy-MM-dd` of the instant in the zone. */
export function dayKeyInZone(date: Date, timeZone: string): string {
  const { year, month, day } = zonedParts(date, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The instant at which the calendar day `year-month-day` starts in the zone (DST-safe). */
export function startOfCalendarDayInZone(year: number, month: number, day: number, timeZone: string): Date {
  let guess = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  for (let i = 0; i < 2; i++) {
    const p = zonedParts(new Date(guess), timeZone);
    const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    guess -= wall - Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  }
  return new Date(guess);
}

/** The last millisecond of the calendar day in the zone. */
export function endOfCalendarDayInZone(year: number, month: number, day: number, timeZone: string): Date {
  return new Date(startOfCalendarDayInZone(year, month, day + 1, timeZone).getTime() - 1);
}

/** Start of the zone's calendar day that contains the instant. */
export function startOfDayInZone(date: Date, timeZone: string): Date {
  const { year, month, day } = zonedParts(date, timeZone);
  return startOfCalendarDayInZone(year, month, day, timeZone);
}

export function endOfDayInZone(date: Date, timeZone: string): Date {
  const { year, month, day } = zonedParts(date, timeZone);
  return endOfCalendarDayInZone(year, month, day, timeZone);
}

/** Parses a `yyyy-MM-dd` day key. */
export function parseDayKey(dayKey: string): { year: number; month: number; day: number } {
  const [year, month, day] = dayKey.split("-").map(Number);
  return { year, month, day };
}

/** `dd.MM.yyyy` in the zone. */
export function formatDayInZone(date: Date, timeZone: string): string {
  const { year, month, day } = zonedParts(date, timeZone);
  return `${pad(day)}.${pad(month)}.${year}`;
}

/** `HH:mm:ss` in the zone. */
export function formatClockInZone(date: Date, timeZone: string): string {
  const { hour, minute, second } = zonedParts(date, timeZone);
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
}

/** `dd.MM.yyyy – HH:mm` in the zone. */
export function formatDateTimeInZone(date: Date, timeZone: string): string {
  const { hour, minute } = zonedParts(date, timeZone);
  return `${formatDayInZone(date, timeZone)} – ${pad(hour)}:${pad(minute)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
